import {
  CAMPAIGN_IMAGE_MAX_BYTES,
  CAMPAIGN_PERIOD_FIELDS,
  CAPABILITY_TAG_PATTERN,
  campaignPeriodStatusSchema,
  campaignPricingModeSchema,
  currencySchema,
  entryLabelOverridesSchema,
  FORM_FIELD_KEY_PATTERN,
  FORM_FILE_MAX_BYTES,
  FORM_SCHEMA_CHANGE_KINDS,
  MAX_CAPABILITY_FIELD_KEYS,
  MAX_FIELDS_PER_SCHEMA,
  MAX_GROUPING_FIELD_KEYS,
  MAX_PRICE_FIELD_KEYS,
  scopeLevelSchema,
} from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { CAMPAIGN_STAFF_STATES, campaignStaffRoleSchema } from './campaign-staff.js';
import { commonErrors, formErrors } from './common/errors.js';
import { formFileSchema, formFileUploadInputSchema } from './common/form-file.js';
import { formSchemaDocumentSchema } from './common/form-schema.js';
import { bulkResultSchema, listRequestSchema, sortedListRequest } from './common/list.js';
import { minorAmountSchema, moneySchema } from './common/money.js';
import { paginated, paginationInputSchema } from './common/pagination.js';
import { queryBooleanSchema } from './common/query.js';
import { campaignQuoteContract, campaignQuoteSchema, quoteAnswersSchema } from './common/quote.js';
import { fileNameSchema, presignedUploadSchema, uploadTokenSchema } from './common/upload.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  TWO_FACTOR_REQUIRED: commonErrors.TWO_FACTOR_REQUIRED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const campaignCollectionScopeSchema = z.enum(['owner_subtree', 'assigned_only']);
export const campaignPortalVisibilitySchema = z.enum(['public', 'unlisted', 'internal']);
export const campaignOverflowPolicySchema = z.enum(['allow', 'stop', 'redirect']);

/**
 * `redirect` stays a schema value and is refused as input: moving a donor's
 * money to a purpose they did not choose waits on a legal answer — see
 * docs/decisions/0017-over-collection-policy.md.
 */
export const campaignOverflowPolicyInputSchema = z.enum(['allow', 'stop']);

export type CampaignOverflowPolicy = z.infer<typeof campaignOverflowPolicySchema>;

export type CampaignCollectionScope = z.infer<typeof campaignCollectionScopeSchema>;
export type CampaignPortalVisibility = z.infer<typeof campaignPortalVisibilitySchema>;

/**
 * English, lowercase snake and stable, matching the *Campaign codes* table in
 * docs/domain-model.md and the 29 rows the seed already carries
 * (`obligatory_qurbani`, `water_well`). Set once at creation and never present
 * on `update`: it is the identity a donation, a report and the audit trail all
 * name.
 */
const campaignDescriptionSchema = z.string().trim().min(1).max(4000);

const campaignCodeSchema = z
  .string()
  .trim()
  .min(3)
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/);

const categoryCodeSchema = z
  .string()
  .trim()
  .min(3)
  .max(50)
  .regex(/^[a-z][a-z0-9_]*$/);

/**
 * A capability tag and a capability field key are both code, not prose: a tag
 * **is** the `value` of one of the campaign's own select options and a key is a
 * field's key, so each carries that thing's own shape — see
 * docs/decisions/0069-an-assignment-says-what-it-can-execute-and-the-campaign-says-which-answers-decide.md.
 */
const capabilityTagSchema = z.string().trim().min(1).max(60).regex(CAPABILITY_TAG_PATTERN);

const capabilityFieldKeySchema = z.string().trim().regex(FORM_FIELD_KEY_PATTERN);

const nameSchema = z.string().trim().min(2).max(160);
const slugSchema = z
  .string()
  .trim()
  .min(2)
  .max(160)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const iconSchema = z.string().trim().min(3).max(80);

export const campaignCategorySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
  campaignCount: z.number().int().min(0),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CampaignCategory = z.infer<typeof campaignCategorySchema>;

/**
 * Categories are global and unscoped: a category is a navigation grouping with
 * no data of its own, and scoping one would only produce empty menus — see
 * docs/decisions/0015-campaign-ownership-scope.md.
 */
export const campaignCategoryContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/campaign-categories',
      summary: 'Campaign categories, in navigation order',
    })
    .errors(guarded)
    .input(
      listRequestSchema.extend({
        includeInactive: queryBooleanSchema.default(false),
      }),
    )
    .output(paginated(campaignCategorySchema)),

  get: oc
    .route({ method: 'GET', path: '/campaign-categories/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ category: campaignCategorySchema })),

  create: oc
    .route({ method: 'POST', path: '/campaign-categories' })
    .errors(guarded)
    .input(
      z.object({
        code: categoryCodeSchema,
        name: nameSchema,
        slug: slugSchema,
        icon: iconSchema.nullable().default(null),
        isActive: z.boolean().default(true),
      }),
    )
    .output(z.object({ category: campaignCategorySchema })),

  update: oc
    .route({ method: 'PATCH', path: '/campaign-categories/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        name: nameSchema.optional(),
        slug: slugSchema.optional(),
        icon: iconSchema.nullable().optional(),
        isActive: z.boolean().optional(),
      }),
    )
    .output(z.object({ category: campaignCategorySchema })),

  /**
   * The **full** ordered list of ids, rewritten in one transaction. A drag
   * sends the whole order rather than a pair of swaps, so the list can never be
   * left half-applied and a drop costs one request instead of one per row.
   */
  reorder: oc
    .route({ method: 'POST', path: '/campaign-categories/order' })
    .errors(guarded)
    .input(z.object({ ids: z.array(z.uuid()).min(1).max(200) }))
    .output(z.object({ items: z.array(campaignCategorySchema) })),
};

export const campaignPeriodSummarySchema = z.object({
  id: z.uuid(),
  seasonYear: z.number().int().nullable(),
  startsAt: z.string(),
  endsAt: z.string(),
  status: campaignPeriodStatusSchema,
  portalVisibility: campaignPortalVisibilitySchema,
  showWhenClosed: z.boolean(),
  /**
   * Status and the calendar window are orthogonal facts: an `active` period
   * before `starts_at` renders as not yet open, so the window is reported
   * beside the status rather than folded into it. Decided server-side against
   * the server clock; see docs/decisions/0016-campaign-period-lifecycle.md.
   */
  isInWindow: z.boolean(),
  hasGoal: z.boolean(),
  goal: moneySchema.nullable(),
  collected: moneySchema.nullable(),
  pledged: moneySchema.nullable(),
});

export type CampaignPeriodSummary = z.infer<typeof campaignPeriodSummarySchema>;

/**
 * The campaign's picture, as an administrator's screen reads it. `url` is a
 * short-lived signed `GET` and is `null` while the scan has not passed or where
 * object storage is not configured — a public page renders the category's icon
 * instead rather than a broken image. The object key is never sent.
 */
export const campaignImageSchema = z.object({
  attachmentId: z.uuid(),
  fileName: z.string(),
  contentType: z.string(),
  fileSize: z.number().int(),
  scanStatus: z.enum(['pending', 'clean', 'infected', 'failed']),
  url: z.string().nullable(),
});

export type CampaignImage = z.infer<typeof campaignImageSchema>;

export const campaignSummarySchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  slug: z.string(),
  categoryId: z.uuid(),
  categoryName: z.string(),
  categoryIcon: z.string().nullable(),
  pricingMode: campaignPricingModeSchema,
  unitLabel: z.string().nullable(),
  unitsPerGroup: z.number().int().nullable(),
  collectionScope: campaignCollectionScopeSchema,
  ownerScopeLevel: scopeLevelSchema,
  ownerScopeId: z.uuid().nullable(),
  ownerScopeName: z.string().nullable(),
  isArchived: z.boolean(),
  formVersion: z.number().int(),
  /**
   * Which of the campaign's own published fields decide which assignments may
   * execute a donation, in the order a screen shows them. Empty offers every
   * active assignment, which is what every campaign did before capabilities
   * existed — docs/decisions/0069-an-assignment-says-what-it-can-execute-and-the-campaign-says-which-answers-decide.md.
   */
  assignmentCapabilityFieldKeys: z.array(z.string()),
  /**
   * Whether a donation form offers *Kendi Kurumum*, the signed-in user's own
   * institution, as the yurt — on for every campaign unless turned off —
   * docs/decisions/0120-kendi-kurumum-is-the-users-own-institution-offered-as-the-yurt.md.
   */
  offersOwnInstitution: z.boolean(),
  /**
   * Which of the campaign's own published checkbox fields says a donation is
   * ready for its period's queue. Null is no gate: nothing is on a waiting
   * list and every approved donation may be queued, which is what every
   * campaign did before the column existed —
   * docs/decisions/0071-a-ready-donation-waits-and-the-order-locks-it.md.
   */
  readinessFieldKey: z.string().nullable(),
  /**
   * Which of the campaign's own published answers the shares of one group
   * must agree on, besides the yurt, which always decides once any is named.
   * Empty groups every `units_per_group` consecutive shares of the order,
   * which is what every campaign did before the column existed —
   * docs/decisions/0025-queue-allocation-and-derived-grouping.md, amended
   * 2026-09-23.
   */
  groupByFieldKeys: z.array(z.string()),
  /**
   * Which of the campaign's own published answers a price may depend on. A
   * season's or an assignment's price for a combination of them names every
   * one; empty means every price is unconditional, which is what every
   * campaign was before the column existed —
   * docs/decisions/0072-a-price-may-depend-on-the-answers-and-a-group-on-the-animal.md.
   */
  priceFieldKeys: z.array(z.string()),
  /**
   * The grouping answer whose options carry their own group size, and the
   * sizes by option value — a sheep 1, a cattle or camel share 7. An answer the
   * map does not name is grouped by `unitsPerGroup`. Null groups every share
   * by `unitsPerGroup`, as every campaign did before the column existed.
   */
  groupSizeFieldKey: z.string().nullable(),
  groupSizes: z.record(z.string(), z.number().int()),
  /**
   * Per-campaign overrides of the single-page entry form's three section
   * headers and its three party labels; a key absent here falls back to the
   * i18n default for that label — docs/plans/11-test-revisions.md §11.7,
   * docs/decisions/0081-a-campaigns-entry-labels-override-the-defaults-free-text-on-the-row.md.
   */
  entryLabels: entryLabelOverridesSchema,
  /**
   * The organization template the queue printout is drawn from for donations
   * of that template's organization, or null for the fixed layout —
   * docs/decisions/0082-a-printout-template-is-an-organizations-ordered-list-of-blocks.md.
   */
  printoutTemplateId: z.uuid().nullable(),
  /**
   * The prose a donor reads before deciding. Turkish content typed by an
   * administrator, not an i18n key — like a form field's label, and for the
   * same reason.
   */
  description: z.string().nullable(),
  /** The season the screens speak about: the `active` one, else the newest. */
  activePeriod: campaignPeriodSummarySchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const campaignSchema = campaignSummarySchema.extend({
  image: campaignImageSchema.nullable(),
  periods: z.array(campaignPeriodSummarySchema),
  /**
   * True once any period has left `draft`. Changing how a donation is priced
   * under donations already captured under the old mode makes historical
   * quantities meaningless — see docs/decisions/0013-campaign-pricing-and-currency.md.
   */
  pricingModeLocked: z.boolean(),
  /**
   * True once any share of the campaign has been queued. Changing the group
   * size then would re-cut groups already handed to a field team, so the
   * server refuses it `CAMPAIGN_UNITS_PER_GROUP_LOCKED` — docs/decisions/0025.
   */
  unitsPerGroupLocked: z.boolean(),
});

export type CampaignSummary = z.infer<typeof campaignSummarySchema>;
export type Campaign = z.infer<typeof campaignSchema>;

export const campaignNavigationCampaignSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  activePeriodId: z.uuid(),
});

export const campaignNavigationCategorySchema = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  icon: z.string().nullable(),
  campaigns: z.array(campaignNavigationCampaignSchema),
});

export type CampaignNavigationCategory = z.infer<typeof campaignNavigationCategorySchema>;

const ownerScopeInput = z.object({
  ownerScopeLevel: scopeLevelSchema.default('global'),
  /**
   * Null only for `global`. The four denormalized scope columns are derived
   * from this node on the server and are never supplied by a client.
   */
  ownerScopeId: z.uuid().nullable().default(null),
});

const positiveMoneySchema = z.object({
  amountMinor: minorAmountSchema.refine((value) => BigInt(value) > 0n, 'must be above zero'),
  currency: currencySchema,
});

const timestampSchema = z.iso.datetime({ offset: true });
const seasonYearSchema = z.number().int().min(2000).max(2100);

export const campaignPeriodSchema = campaignPeriodSummarySchema.extend({
  campaignId: z.uuid(),
  overflowPolicy: campaignOverflowPolicySchema,
  acceptsBackdatedUntil: z.string().nullable(),
  collectedByCurrency: z.array(moneySchema),
  /** Decided by the state machine on the server; the screen only offers these. */
  nextStatuses: z.array(campaignPeriodStatusSchema),
  editableFields: z.array(z.enum(CAMPAIGN_PERIOD_FIELDS)),
  pricesEditable: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CampaignPeriod = z.infer<typeof campaignPeriodSchema>;

/**
 * The answers one price is for: `{}` for the unconditional price, or every one
 * of the campaign's price-deciding keys with an option value, or `true` or
 * `false` for a checkbox.
 */
export const priceAnswerConditionsSchema = z
  .record(capabilityFieldKeySchema, z.union([z.string().min(1).max(120), z.boolean()]))
  .refine((conditions) => Object.keys(conditions).length <= MAX_PRICE_FIELD_KEYS);

export const campaignPeriodPriceSchema = z.object({
  id: z.uuid(),
  campaignPeriodId: z.uuid(),
  /**
   * Null on the season's own price; an assignment id on that assignment's
   * override of it — docs/decisions/0068-a-price-may-be-overridden-per-assignment.md.
   */
  campaignAssignmentId: z.uuid().nullable(),
  currency: currencySchema,
  unitAmountMinor: minorAmountSchema.nullable(),
  minAmountMinor: minorAmountSchema.nullable(),
  maxAmountMinor: minorAmountSchema.nullable(),
  suggestedAmountsMinor: z.array(minorAmountSchema).nullable(),
  /** `{}` for an unconditional price; otherwise the answers this price is for. */
  answerConditions: z.record(z.string(), z.union([z.string(), z.boolean()])),
  isDefault: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CampaignPeriodPrice = z.infer<typeof campaignPeriodPriceSchema>;

const campaignPricesContract = {
  /**
   * The season's own prices, or — with `campaignAssignmentId` — that one
   * assignment's overrides of them. Never both in one page: they are two
   * questions, and a list mixing them would read as a season pricing one
   * currency twice.
   */
  list: oc
    .route({ method: 'GET', path: '/campaign-periods/{campaignPeriodId}/prices' })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        campaignPeriodId: z.uuid(),
        campaignAssignmentId: z.uuid().optional(),
      }),
    )
    .output(paginated(campaignPeriodPriceSchema)),

  /**
   * One row per `(period, assignment, currency, answers)`, the assignment null
   * for the season's own price and the answers `{}` for the unconditional one.
   * Making a row the default clears the previous default in the same
   * transaction, because the partial unique index allows exactly one; the
   * columns the campaign's pricing mode does not read are stored as null. An
   * override or a price for some answers may not be the default and needs the
   * season's unconditional price in its currency —
   * docs/decisions/0068-a-price-may-be-overridden-per-assignment.md and
   * docs/decisions/0072-a-price-may-depend-on-the-answers-and-a-group-on-the-animal.md.
   * Answers naming anything but every price-deciding field of the campaign,
   * each with an answer it offers, are refused `CAMPAIGN_PRICE_CONDITION_INVALID`.
   */
  upsert: oc
    .route({ method: 'PUT', path: '/campaign-periods/{campaignPeriodId}/prices/{currency}' })
    .errors(guarded)
    .input(
      z.object({
        campaignPeriodId: z.uuid(),
        currency: currencySchema,
        campaignAssignmentId: z.uuid().optional(),
        answerConditions: priceAnswerConditionsSchema.default({}),
        unitAmountMinor: minorAmountSchema.nullable().default(null),
        minAmountMinor: minorAmountSchema.nullable().default(null),
        maxAmountMinor: minorAmountSchema.nullable().default(null),
        suggestedAmountsMinor: z.array(minorAmountSchema).max(12).nullable().default(null),
        isDefault: z.boolean().default(false),
      }),
    )
    .output(z.object({ price: campaignPeriodPriceSchema })),

  remove: oc
    .route({ method: 'DELETE', path: '/campaign-periods/{campaignPeriodId}/prices/{currency}' })
    .errors(guarded)
    .input(
      z.object({
        campaignPeriodId: z.uuid(),
        currency: currencySchema,
        campaignAssignmentId: z.uuid().optional(),
        answerConditions: priceAnswerConditionsSchema.default({}),
      }),
    )
    .output(z.object({ currency: currencySchema })),
};

const periodMeasuresSchema = z.object({
  pledged: moneySchema,
  collected: moneySchema,
  fulfillmentCost: moneySchema,
});

export const campaignPeriodCurrencyTotalsSchema = periodMeasuresSchema.extend({
  currency: currencySchema,
  donationCount: z.number().int().min(0),
  updatedAt: z.string(),
});

/**
 * Three measures, never one, per currency and unconverted — see
 * docs/decisions/0014-campaign-goals-and-totals.md. `inGoalCurrency` is null
 * for a period without a goal. Every other currency is converted into the goal
 * currency at the latest `exchange_rates` day on which each rate it needs
 * exists; `convertedAt` is that rate day (`YYYY-MM-DD`), set only when a rate
 * was applied, so the figure is never mistaken for a historical one.
 * `unconvertedCurrencies` names every currency left out because no rate for it
 * exists — PLN, CZK and BGN, which TCMB does not quote.
 */
export const campaignPeriodTotalsSchema = z.object({
  campaignPeriodId: z.uuid(),
  hasGoal: z.boolean(),
  goal: moneySchema.nullable(),
  byCurrency: z.array(campaignPeriodCurrencyTotalsSchema),
  inGoalCurrency: periodMeasuresSchema
    .extend({
      unconvertedCurrencies: z.array(currencySchema),
      convertedAt: z.string().nullable(),
    })
    .nullable(),
});

export type CampaignPeriodTotals = z.infer<typeof campaignPeriodTotalsSchema>;

const recomputedMeasuresSchema = z.object({
  pledged: moneySchema,
  collected: moneySchema,
  fulfillmentCost: moneySchema,
  donationCount: z.number().int(),
});

/**
 * One currency the rollup disagreed with its sources about, with both sides of
 * the disagreement. `pledged`, `fulfillmentCost` and `donationCount` are rebuilt
 * from `donations`, and `collected` from the confirmed matches in
 * `reconciliations`.
 */
export const campaignPeriodDriftSchema = z.object({
  currency: currencySchema,
  stored: recomputedMeasuresSchema,
  recomputed: recomputedMeasuresSchema,
});

export type CampaignPeriodDrift = z.infer<typeof campaignPeriodDriftSchema>;

/**
 * What a recompute did. `unchanged` means the rollup already agreed with
 * `donations`, and writes nothing; `recomputed` names every currency that
 * disagreed and is the one case that writes an audit row — see
 * docs/decisions/0014-campaign-goals-and-totals.md.
 */
export const campaignPeriodRecomputeSchema = z.object({
  campaignPeriodId: z.uuid(),
  outcome: z.enum(['unchanged', 'recomputed']),
  drift: z.array(campaignPeriodDriftSchema),
});

export type CampaignPeriodRecompute = z.infer<typeof campaignPeriodRecomputeSchema>;

/**
 * Everything campaign-scoped from here on names a period, never a campaign
 * alone — see docs/decisions/0011-campaign-periods-over-season-clones.md.
 */
export const campaignPeriodContract = {
  list: oc
    .route({ method: 'GET', path: '/campaigns/{campaignId}/periods' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ campaignId: z.uuid() }))
    .output(paginated(campaignPeriodSchema)),

  get: oc
    .route({ method: 'GET', path: '/campaign-periods/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ period: campaignPeriodSchema })),

  create: oc
    .route({ method: 'POST', path: '/campaigns/{campaignId}/periods' })
    .errors(guarded)
    .input(
      z.object({
        campaignId: z.uuid(),
        seasonYear: seasonYearSchema,
        startsAt: timestampSchema,
        endsAt: timestampSchema,
        goal: positiveMoneySchema.nullable().default(null),
        overflowPolicy: campaignOverflowPolicyInputSchema.default('allow'),
        portalVisibility: campaignPortalVisibilitySchema.default('public'),
        showWhenClosed: z.boolean().default(false),
      }),
    )
    .output(z.object({ period: campaignPeriodSchema })),

  /** Which fields may change depends on the status; see `editableFields`. */
  update: oc
    .route({ method: 'PATCH', path: '/campaign-periods/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        seasonYear: seasonYearSchema.optional(),
        startsAt: timestampSchema.optional(),
        endsAt: timestampSchema.optional(),
        goal: positiveMoneySchema.nullable().optional(),
        overflowPolicy: campaignOverflowPolicyInputSchema.optional(),
        portalVisibility: campaignPortalVisibilitySchema.optional(),
        showWhenClosed: z.boolean().optional(),
        acceptsBackdatedUntil: timestampSchema.optional(),
      }),
    )
    .output(z.object({ period: campaignPeriodSchema })),

  /**
   * There is no un-closing: a reopened season is a new period. Closing without
   * `acceptsBackdatedUntil` accepts late paper forms for seven days — see
   * docs/decisions/0016-campaign-period-lifecycle.md.
   */
  transition: oc
    .route({ method: 'POST', path: '/campaign-periods/{id}/transition' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        to: campaignPeriodStatusSchema,
        acceptsBackdatedUntil: timestampSchema.optional(),
      }),
    )
    .output(z.object({ period: campaignPeriodSchema })),

  /**
   * Opens next season in one transaction: a draft period, its price rows
   * optionally uplifted and rounded half-up, and its assignments — without
   * people, who are the campaign's staff assignments (docs/decisions/0095). The campaign's form schema is not copied — every season reads
   * the one the campaign carries.
   */
  duplicate: oc
    .route({ method: 'POST', path: '/campaign-periods/{sourcePeriodId}/duplicate' })
    .errors(guarded)
    .input(
      z.object({
        sourcePeriodId: z.uuid(),
        seasonYear: seasonYearSchema,
        startsAt: timestampSchema,
        endsAt: timestampSchema,
        copyPrices: z.boolean().default(true),
        copyAssignments: z.boolean().default(true),
        priceIncreasePercent: z.number().min(0).max(1000).optional(),
      }),
    )
    .output(
      z.object({
        period: campaignPeriodSchema,
        copiedPrices: z.number().int().min(0),
        copiedAssignments: z.number().int().min(0),
      }),
    ),

  prices: campaignPricesContract,

  totals: oc
    .route({ method: 'GET', path: '/campaign-periods/{id}/totals' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ totals: campaignPeriodTotalsSchema })),

  /**
   * The backstop that rebuilds the rollup from `donations` in one transaction
   * and audits any drift it finds. It is the manual half of
   * docs/decisions/0014-campaign-goals-and-totals.md's detector; the nightly
   * job that calls it for every open period waits on `apps/worker`.
   */
  recomputeTotals: oc
    .route({ method: 'POST', path: '/campaign-periods/{id}/totals/recompute' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(campaignPeriodRecomputeSchema),
};

/**
 * **Who works a season's yurt, read-only** — the staff assignment for the
 * campaign at the yurt's institution, which stands across seasons and is
 * edited on *Kampanya Görevlendirmeleri* (docs/decisions/0095). The season
 * names nobody; this is the one layer that does.
 */
export const campaignAssignmentStaffingSchema = z.object({
  staffAssignmentId: z.uuid(),
  state: z.enum(CAMPAIGN_STAFF_STATES),
  users: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      isActive: z.boolean(),
      role: campaignStaffRoleSchema,
    }),
  ),
});

export type CampaignAssignmentStaffing = z.infer<typeof campaignAssignmentStaffingSchema>;

export const campaignAssignmentSchema = z.object({
  id: z.uuid(),
  campaignPeriodId: z.uuid(),
  institutionId: z.uuid(),
  institutionCode: z.string(),
  countryName: z.string(),
  /**
   * The name this campaign shows for the institution: the override when one is
   * set, otherwise the institution's own name. Resolved on the server, so no
   * screen falls back to the raw name by itself.
   */
  displayName: z.string(),
  displayNameOverride: z.string().nullable(),
  isActive: z.boolean(),
  /**
   * *Formda Göster*: whether a donation form offers this yurt as the place the
   * work is done. A hidden yurt still executes what already names it
   * (docs/decisions/0115).
   */
  showOnForm: z.boolean(),
  /**
   * What one unit of work carried out here is worth to this institution — the
   * inter-institution ledger's amount per unit at fulfilment. Null books
   * nothing (docs/decisions/0115).
   */
  unitCost: moneySchema.nullable(),
  /**
   * What this assignment can execute, lowercase and in tag order. Free-form,
   * because no fixed vocabulary covers a qurbani, a recitation and a well at
   * once — docs/decisions/0069-an-assignment-says-what-it-can-execute-and-the-campaign-says-which-answers-decide.md.
   */
  capabilities: z.array(z.string()),
  /** Null when nobody staffs the campaign at this institution. */
  staffing: campaignAssignmentStaffingSchema.nullable(),
  /**
   * Whether the staffing is in force and names an active primary — the
   * question leaving `draft` asks of an active yurt, answered by the server so
   * the screen shows what the transition will say (docs/decisions/0035, 0095).
   */
  hasPrimaryStaff: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type CampaignAssignment = z.infer<typeof campaignAssignmentSchema>;

export const campaignAssignmentInstitutionOptionSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
  countryName: z.string(),
  assigned: z.boolean(),
});

/**
 * One yurt the entry form may offer, with the amount it comes to there. The
 * display name is the server's, never the institution's own — *Assignment* in
 * .claude/rules/campaign-engine.md. `quote` is null when the chosen amount
 * falls outside that yurt's own bounds: the yurt is still listed, because the
 * capability answer says it can do the work, and the save refuses the amount.
 */
export const campaignEligibleAssignmentSchema = z.object({
  assignmentId: z.uuid(),
  institutionId: z.uuid(),
  displayName: z.string(),
  capabilities: z.array(z.string()),
  quote: campaignQuoteSchema.nullable(),
});

export type CampaignEligibleAssignment = z.infer<typeof campaignEligibleAssignmentSchema>;

/**
 * *Kendi Kurumum*: the signed-in user's own institution as the entry form's
 * first yurt, decided and priced by the server, and always choosable while the
 * campaign offers it (docs/decisions/0123). `assignmentId` is the season's
 * active assignment of the institution when it holds one, shown on forms or
 * not, and then the quote carries that assignment's override; null is an
 * institution with no active assignment, quoted at the season's own price.
 */
export const campaignOwnInstitutionOptionSchema = z.object({
  institutionId: z.uuid(),
  displayName: z.string(),
  assignmentId: z.uuid().nullable(),
  quote: campaignQuoteSchema.nullable(),
});

export type CampaignOwnInstitutionOption = z.infer<typeof campaignOwnInstitutionOptionSchema>;

const displayNameSchema = z.string().trim().min(2).max(160);

/** A unit cost is a positive whole number of minor units in one currency. */
export const assignmentUnitCostSchema = z.object({
  amountMinor: z.string().regex(/^[1-9]\d{0,17}$/, 'must be a positive integer in minor units'),
  currency: currencySchema,
});

/**
 * Assignment governs who **executes** a season, never who may collect for it
 * — see docs/decisions/0015-campaign-ownership-scope.md. Every write names an
 * institution inside the caller's scope and passes the campaign's write check.
 */
export const campaignAssignmentContract = {
  list: oc
    .route({ method: 'GET', path: '/campaign-periods/{campaignPeriodId}/assignments' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ campaignPeriodId: z.uuid() }))
    .output(paginated(campaignAssignmentSchema)),

  /** The four scope columns are derived from the institution on the server. */
  create: oc
    .route({ method: 'POST', path: '/campaign-periods/{campaignPeriodId}/assignments' })
    .errors(guarded)
    .input(
      z.object({
        campaignPeriodId: z.uuid(),
        institutionId: z.uuid(),
        displayName: displayNameSchema.nullable().default(null),
        showOnForm: z.boolean().default(true),
        unitCost: assignmentUnitCostSchema.nullable().default(null),
      }),
    )
    .output(z.object({ assignment: campaignAssignmentSchema })),

  /** `displayName: null` clears the override and the institution's own name shows again. */
  update: oc
    .route({ method: 'PATCH', path: '/campaign-assignments/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        displayName: displayNameSchema.nullable().optional(),
        isActive: z.boolean().optional(),
        showOnForm: z.boolean().optional(),
        /** `null` clears the cost; fulfilments already booked keep theirs. */
        unitCost: assignmentUnitCostSchema.nullable().optional(),
      }),
    )
    .output(z.object({ assignment: campaignAssignmentSchema })),

  remove: oc
    .route({ method: 'DELETE', path: '/campaign-assignments/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ id: z.uuid() })),

  /**
   * Copies another season's assignments of the same campaign into this one in
   * a single transaction. An institution this season already assigns is
   * skipped. No person is copied: who works a yurt is the campaign's staff
   * assignment there, which stands across seasons (docs/decisions/0095).
   */
  copyFromPeriod: oc
    .route({ method: 'POST', path: '/campaign-periods/{campaignPeriodId}/assignments/copy' })
    .errors(guarded)
    .input(z.object({ campaignPeriodId: z.uuid(), sourcePeriodId: z.uuid() }))
    .output(
      z.object({
        copiedAssignments: z.number().int().min(0),
        skippedAssignments: z.number().int().min(0),
      }),
    ),

  institutionOptions: oc
    .route({
      method: 'GET',
      path: '/campaign-periods/{campaignPeriodId}/assignment-options/institutions',
    })
    .errors(guarded)
    .input(
      paginationInputSchema.extend({
        campaignPeriodId: z.uuid(),
        search: z.string().trim().optional(),
      }),
    )
    .output(paginated(campaignAssignmentInstitutionOptionSchema)),

  /**
   * Which of a season's active assignments may execute a donation whose answers
   * are these, each with the price 0068 would quote it at — so the entry form
   * offers only the yurts that can carry the work out and shows the price beside
   * each without a second round trip.
   *
   * It is a `POST` although it changes nothing: the input carries a whole
   * `form_data` document, which does not fit a query string. It is guarded,
   * scoped and gated exactly as `campaign.staffQuote` is, so a screen is never
   * shown a yurt or a price the save would refuse. **There is no public
   * sibling**: the answer carries every eligible dormitory's display name and
   * price sheet, which is more than 0068 let the donor's quote disclose —
   * docs/decisions/0069-an-assignment-says-what-it-can-execute-and-the-campaign-says-which-answers-decide.md.
   */
  eligible: oc
    .route({
      method: 'POST',
      path: '/campaign-periods/{campaignPeriodId}/eligible-assignments',
    })
    .errors({ ...guarded, PRICING_VIOLATION: commonErrors.PRICING_VIOLATION })
    .input(
      paginationInputSchema.extend({
        campaignPeriodId: z.uuid(),
        currency: currencySchema,
        /** The answers so far. An unanswered field narrows nothing. */
        formData: z.record(z.string(), z.unknown()).default({}),
        /**
         * What `campaign.staffQuote` would be sent. A unit-priced season asked
         * without a quantity is refused `PRICING_VIOLATION` / `QUANTITY_INVALID`
         * rather than answered unpriced; an amount-priced one asked before the
         * amount is typed offers every yurt with `quote: null`.
         */
        quantity: z.number().int().positive().optional(),
        amountMinor: minorAmountSchema.optional(),
      }),
    )
    .output(
      paginated(campaignEligibleAssignmentSchema).extend({
        /** Null when the campaign does not offer *Kendi Kurumum*. */
        ownInstitution: campaignOwnInstitutionOptionSchema.nullable(),
      }),
    ),

  capabilities: {
    /**
     * Replaces the whole capability set of one assignment in a single
     * transaction — a tag an operator unticks is removed and a new one
     * inserted. The database refuses a duplicate and a
     * malformed tag, so neither can arrive by another route.
     */
    set: oc
      .route({ method: 'PUT', path: '/campaign-assignments/{assignmentId}/capabilities' })
      .errors(guarded)
      .input(
        z.object({
          assignmentId: z.uuid(),
          capabilities: z.array(capabilityTagSchema).max(40),
        }),
      )
      .output(z.object({ assignment: campaignAssignmentSchema })),
  },
};

const formPublisherSchema = z.object({ id: z.uuid(), name: z.string() });

export const campaignFormVersionSchema = z.object({
  version: z.number().int().min(1),
  publishedAt: z.string(),
  /** Resolved to a display name on the server; null for a version the seed wrote. */
  publishedBy: formPublisherSchema.nullable(),
  changeSummary: z.string().nullable(),
});

export type CampaignFormVersion = z.infer<typeof campaignFormVersionSchema>;

export const campaignFormSchemaSchema = z.object({
  campaignId: z.uuid(),
  version: z.number().int().min(0),
  currentVersion: z.number().int().min(0),
  document: formSchemaDocumentSchema,
  publishedAt: z.string().nullable(),
  publishedBy: formPublisherSchema.nullable(),
  changeSummary: z.string().nullable(),
});

export type CampaignFormSchema = z.infer<typeof campaignFormSchemaSchema>;

const formChangeValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string()),
  z.object({
    field: z.string(),
    equals: z.union([z.string(), z.number(), z.boolean(), z.null()]),
  }),
]);

export const formSchemaChangeSchema = z.object({
  key: z.string().nullable(),
  kind: z.enum(FORM_SCHEMA_CHANGE_KINDS),
  breaking: z.boolean(),
  rule: z.string().nullable(),
  option: z.string().nullable(),
  from: formChangeValueSchema,
  to: formChangeValueSchema,
});

export const formSchemaDiffSchema = z.object({
  breaking: z.boolean(),
  changes: z.array(formSchemaChangeSchema),
});

export type FormSchemaDiffDto = z.infer<typeof formSchemaDiffSchema>;

export const campaignFormPublishResultSchema = z.object({
  dryRun: z.boolean(),
  previousVersion: z.number().int().min(0),
  version: z.number().int().min(1),
  inPlace: z.boolean(),
  diff: formSchemaDiffSchema,
  changeSummary: z.string(),
  document: formSchemaDocumentSchema,
});

export type CampaignFormPublishResult = z.infer<typeof campaignFormPublishResultSchema>;

/**
 * The form belongs to the campaign, not to a period: every season reads the
 * one the campaign carries — see docs/decisions/0011-campaign-periods-over-season-clones.md.
 */
export const campaignFormSchemaContract = {
  /** Defaults to the current version. Version 0 is the empty, never-published form. */
  get: oc
    .route({ method: 'GET', path: '/campaigns/{campaignId}/form-schema' })
    .errors(guarded)
    .input(
      z.object({
        campaignId: z.uuid(),
        version: z.coerce.number().int().min(0).optional(),
      }),
    )
    .output(z.object({ formSchema: campaignFormSchemaSchema })),

  versions: oc
    .route({ method: 'GET', path: '/campaigns/{campaignId}/form-schema/versions' })
    .errors(guarded)
    .input(paginationInputSchema.extend({ campaignId: z.uuid() }))
    .output(paginated(campaignFormVersionSchema)),

  /**
   * `document.fields` is typed loosely here on purpose: it is validated by
   * `formSchemaMetaSchema` in the service, so a rejection arrives as 422 with
   * the `FORM_*` code of each offending field rather than as a bare 400.
   * `document.version` is the version the editor started from, and a publish
   * made from a stale one is refused. `dryRun` returns the same answer without
   * writing, so the builder can warn before the administrator commits.
   */
  publish: oc
    .route({ method: 'POST', path: '/campaigns/{campaignId}/form-schema' })
    .errors({ ...guarded, ...formErrors })
    .input(
      z.object({
        campaignId: z.uuid(),
        document: z.object({
          version: z.number().int().min(0),
          fields: z.array(z.unknown()).max(MAX_FIELDS_PER_SCHEMA),
        }),
        dryRun: z.boolean().default(false),
      }),
    )
    .output(campaignFormPublishResultSchema),
};

/**
 * `code` names why a row was refused — `NOT_FOUND` for one the caller cannot
 * see, `OUT_OF_SCOPE` for one they may read but not write — and is `UNCHANGED`
 * on a row that already held the requested state.
 */
export const campaignArchiveResultSchema = z.object({
  id: z.uuid(),
  ok: z.boolean(),
  code: z.string().nullable(),
});

export type CampaignArchiveResult = z.infer<typeof campaignArchiveResultSchema>;

/**
 * Columns of `campaigns` itself. The category name and the active period are
 * shown on the row but not offered here: the first sorts a joined dictionary
 * row and the second is one of many periods picked per campaign, so neither
 * has a single value to order the page by.
 */
export const CAMPAIGN_SORT_FIELDS = ['name', 'code', 'createdAt'] as const;

export type CampaignSortField = (typeof CAMPAIGN_SORT_FIELDS)[number];

/**
 * A campaign form's own `file` field, in the three steps §5.4's statement
 * import established: ask for a presigned `PUT`, upload straight to the store,
 * then name the token here. The server reads the object back, decides its type
 * **from the bytes**, writes it to a key only it chose and forgets the
 * browser's — .claude/rules/file-uploads.md.
 *
 * The file is committed **before** the donation exists, because a form is
 * filled in before it is submitted. The `attachments` row it creates belongs to
 * the campaign whose form asked for it, and the field's value is its id.
 */
export const campaignFormFileContract = {
  requestUpload: oc
    .route({ method: 'POST', path: '/campaigns/{campaignId}/form-files/uploads' })
    .errors(guarded)
    .input(
      formFileUploadInputSchema.extend({
        campaignId: z.uuid(),
        fileName: fileNameSchema,
        fileSize: z.number().int().min(1).max(FORM_FILE_MAX_BYTES),
      }),
    )
    .output(presignedUploadSchema),

  /**
   * The token names the campaign it was issued for, so the commit takes no
   * campaign of its own: what a caller may commit is decided by the same read
   * that signed the `PUT`, asked again against the campaign the token names.
   */
  commit: oc
    .route({ method: 'POST', path: '/campaigns/form-files' })
    .errors(guarded)
    .input(z.object({ uploadToken: uploadTokenSchema }))
    .output(z.object({ file: formFileSchema })),
};

export const campaignContract = {
  list: oc
    .route({
      method: 'GET',
      path: '/campaigns',
      summary: 'Campaigns visible to the caller through the inverted owner predicate',
    })
    .errors(guarded)
    .input(
      sortedListRequest(CAMPAIGN_SORT_FIELDS, 'name', 'asc').extend({
        categoryId: z.uuid().optional(),
        status: campaignPeriodStatusSchema.optional(),
        ownerScopeLevel: scopeLevelSchema.optional(),
        seasonYear: z.coerce.number().int().min(2000).max(2100).optional(),
        /** Archived campaigns are hidden by default; deletion does not exist. */
        includeInactive: queryBooleanSchema.default(false),
      }),
    )
    .output(paginated(campaignSummarySchema)),

  get: oc
    .route({ method: 'GET', path: '/campaigns/{id}' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ campaign: campaignSchema })),

  /**
   * Writes the campaign **and its first period** in one transaction: a campaign
   * with no period is not a valid state, so no screen has to handle one — see
   * docs/decisions/0011-campaign-periods-over-season-clones.md.
   */
  create: oc
    .route({ method: 'POST', path: '/campaigns' })
    .errors(guarded)
    .input(
      ownerScopeInput.extend({
        categoryId: z.uuid(),
        code: campaignCodeSchema,
        name: nameSchema,
        slug: slugSchema,
        pricingMode: campaignPricingModeSchema.default('unit'),
        unitLabel: z.string().trim().max(60).nullable().default(null),
        unitsPerGroup: z.number().int().min(1).max(1000).nullable().default(null),
        collectionScope: campaignCollectionScopeSchema.default('owner_subtree'),
        description: campaignDescriptionSchema.nullable().default(null),
      }),
    )
    .output(z.object({ campaign: campaignSchema })),

  /** `code` is absent on purpose: it is immutable after creation. */
  update: oc
    .route({ method: 'PATCH', path: '/campaigns/{id}' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        categoryId: z.uuid().optional(),
        name: nameSchema.optional(),
        slug: slugSchema.optional(),
        pricingMode: campaignPricingModeSchema.optional(),
        unitLabel: z.string().trim().max(60).nullable().optional(),
        unitsPerGroup: z.number().int().min(1).max(1000).nullable().optional(),
        collectionScope: campaignCollectionScopeSchema.optional(),
        ownerScopeLevel: scopeLevelSchema.optional(),
        ownerScopeId: z.uuid().nullable().optional(),
        description: campaignDescriptionSchema.nullable().optional(),
        /**
         * The whole ordered list, replaced. Every key must name a published
         * `select`, `radio` or `multiselect` field of this campaign, or the
         * write is refused `CAMPAIGN_CAPABILITY_FIELD_UNKNOWN` — a typo is
         * caught where the campaign is configured rather than where a clerk
         * cannot find a yurt.
         */
        assignmentCapabilityFieldKeys: z
          .array(capabilityFieldKeySchema)
          .max(MAX_CAPABILITY_FIELD_KEYS)
          .optional(),
        /** Whether a donation form offers *Kendi Kurumum* as the yurt. */
        offersOwnInstitution: z.boolean().optional(),
        /**
         * The readiness field, or null for none. A key naming no published
         * `checkbox` of this campaign is refused `CAMPAIGN_READINESS_FIELD_UNKNOWN`,
         * naming the key.
         */
        readinessFieldKey: capabilityFieldKeySchema.nullable().optional(),
        /**
         * The whole ordered list of grouping answers, replaced. Every key must
         * name a published `select`, `radio` or `checkbox` of this campaign,
         * once, or the write is refused `CAMPAIGN_GROUPING_FIELD_UNKNOWN`; and
         * the list is fixed once any share of the campaign has been fulfilled,
         * `CAMPAIGN_GROUPING_FIELDS_LOCKED`, because changing it regroups
         * animals already carried out.
         */
        groupByFieldKeys: z.array(capabilityFieldKeySchema).max(MAX_GROUPING_FIELD_KEYS).optional(),
        /**
         * The whole ordered list of price-deciding answers, replaced. Every key
         * must name a published `select`, `radio` or `checkbox` of this
         * campaign, once, or the write is refused `CAMPAIGN_PRICE_FIELD_UNKNOWN`;
         * and while any season prices a combination of the current list, a new
         * list is refused `CAMPAIGN_PRICE_FIELDS_IN_USE`.
         */
        priceFieldKeys: z.array(capabilityFieldKeySchema).max(MAX_PRICE_FIELD_KEYS).optional(),
        /**
         * The grouping answer whose options carry their own group size, or
         * null for none. It must be a published `select` or `radio` that is
         * one of `groupByFieldKeys` — so no animal holds shares of two sizes —
         * on a campaign with a `unitsPerGroup`, or the write is refused
         * `CAMPAIGN_GROUP_SIZE_FIELD_UNKNOWN` or
         * `CAMPAIGN_GROUP_SIZE_FIELD_NOT_GROUPED`; fixed once a share of the
         * campaign is fulfilled, `CAMPAIGN_GROUPING_FIELDS_LOCKED`.
         */
        groupSizeFieldKey: capabilityFieldKeySchema.nullable().optional(),
        /**
         * The whole map of sizes by option value, replaced. A key that is not
         * one of the field's options is refused `CAMPAIGN_GROUP_SIZE_OPTION_UNKNOWN`.
         */
        groupSizes: z
          .record(z.string().min(1).max(120), z.number().int().min(1).max(1000))
          .optional(),
        /**
         * The whole map of label overrides, replaced. A key absent or sent
         * blank falls back to the i18n default for that label — the entry
         * form's three section headers and its three party labels —
         * docs/plans/11-test-revisions.md §11.7.
         */
        entryLabels: entryLabelOverridesSchema.optional(),
        /**
         * A template of an organization the caller's scope wholly holds, or
         * null for the fixed layout — docs/plans/11-test-revisions.md §11.8.
         */
        printoutTemplateId: z.uuid().nullable().optional(),
      }),
    )
    .output(z.object({ campaign: campaignSchema })),

  /**
   * The picture a donor sees, in the three steps §5.4's statement import
   * established and this kind copies: ask for a presigned `PUT`, upload
   * straight to the store, then name the token here. The server reads the
   * object back, decides its type **from the bytes**, writes it to a key only
   * it chose and forgets the browser's — .claude/rules/file-uploads.md.
   */
  requestImageUpload: oc
    .route({ method: 'POST', path: '/campaigns/{id}/image-uploads' })
    .errors(guarded)
    .input(
      z.object({
        id: z.uuid(),
        fileName: fileNameSchema,
        fileSize: z.number().int().min(1).max(CAMPAIGN_IMAGE_MAX_BYTES),
      }),
    )
    .output(presignedUploadSchema),

  setImage: oc
    .route({ method: 'PUT', path: '/campaigns/{id}/image' })
    .errors(guarded)
    .input(z.object({ id: z.uuid(), uploadToken: uploadTokenSchema }))
    .output(z.object({ campaign: campaignSchema })),

  /** The row stays and records what was held; only the object is destroyed. */
  removeImage: oc
    .route({ method: 'DELETE', path: '/campaigns/{id}/image' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ campaign: campaignSchema })),

  /**
   * Archival replaces deletion, which does not exist. The type leaves the
   * navigation and the pickers; its periods, donations and reports stay exactly
   * as they were.
   */
  archive: oc
    .route({ method: 'POST', path: '/campaigns/{id}/archive' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ campaign: campaignSchema })),

  restore: oc
    .route({ method: 'POST', path: '/campaigns/{id}/restore' })
    .errors(guarded)
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ campaign: campaignSchema })),

  /**
   * The list screen's bulk action. One call carries the whole selection and
   * answers per row, so a selection mixing campaigns the caller may write with
   * ones they may not reports exactly which were refused instead of failing
   * wholesale — see .claude/rules/list-screen-pattern.md.
   */
  setArchived: oc
    .route({ method: 'POST', path: '/campaigns/archival' })
    .errors(guarded)
    .input(
      z.object({
        ids: z.array(z.uuid()).min(1).max(100),
        isArchived: z.boolean(),
      }),
    )
    .output(bulkResultSchema(campaignArchiveResultSchema)),

  period: campaignPeriodContract,

  assignment: campaignAssignmentContract,

  formSchema: campaignFormSchemaContract,

  formFile: campaignFormFileContract,

  /**
   * The only endpoint that computes a donation amount. Public and
   * permissionless, because the portal needs a price before anyone signs in;
   * it reads a published, open period and writes nothing. For a `unit`
   * campaign any `amountMinor` sent is ignored — see
   * docs/decisions/0013-campaign-pricing-and-currency.md.
   */
  /**
   * The staff sibling of `quote`. It prices what a member of staff may actually
   * enter — an `internal` period, and a closed one still inside its backdating
   * window — and refuses everything else the same way. The public route is not
   * widened for it: a donor may not price what has never been published, and a
   * clerk must be able to type in Friday's paper forms on Monday. See
   * docs/decisions/0032-quote-refusals-and-period-duplication.md and
   * docs/decisions/0027-donated-at-and-backdated-entry.md.
   */
  staffQuote: oc
    .route({ method: 'GET', path: '/campaigns/staff-quote' })
    .errors({
      ...guarded,
      PRICING_VIOLATION: commonErrors.PRICING_VIOLATION,
      DONATION_BACKDATE_NOT_PERMITTED: commonErrors.DONATION_BACKDATE_NOT_PERMITTED,
    })
    .input(
      z.object({
        campaignPeriodId: z.uuid(),
        currency: currencySchema,
        /**
         * The institution the donation will be fulfilled at. It must be an
         * active assignment of the period, or the quote refuses rather than
         * quietly pricing at the season's default.
         */
        fulfillmentInstitutionId: z.uuid().optional(),
        quantity: z.coerce.number().optional(),
        amountMinor: minorAmountSchema.optional(),
        /** The answers so far; only the campaign's price-deciding ones are read. */
        answers: quoteAnswersSchema.optional(),
        /**
         * The donation a correction is priced for. Read through the caller's
         * donation scope, `NOT_FOUND` outside it, so the yurt it already names
         * in the same season is quoted as the save accepts it — a value the
         * row already has, sent back unchanged, is not new work
         * (docs/decisions/0120).
         */
        donationId: z.uuid().optional(),
      }),
    )
    .output(campaignQuoteSchema),

  quote: campaignQuoteContract,
};
